import {FC, useMemo, useState} from "react";
import {App, Button, Flex, Input, Popover, Select} from "antd";
import {
    CheckCircleOutlined,
    DeleteOutlined,
    RightCircleOutlined,
    RightOutlined,
    SendOutlined,
    StopOutlined
} from "@ant-design/icons";
import {Event, User} from "../../types/domain.ts";
import {eventsEventIdApprovePost} from "../../api/events/eventsEventIdApprovePost.ts";
import {eventsEventIdReturnPost} from "../../api/events/eventsEventIdReturnPost.ts";
import {useUsers} from "../../queries/useUsers.ts";
import {useOwnProfile} from "../../stores/OwnProfileStore.ts";
import {eventsEventIdForwardPost} from "../../api/events/eventsEventIdForwardPost.ts";
import styles from './EventActions.module.css';
import {eventsEventIdUndraftPut} from "../../api/events/eventsEventIdUndraftPut.ts";
import {eventsEventIdUnreturnPost} from "../../api/events/eventsEventIdUnreturnPost.ts";
import {useNavigate} from "react-router";
import {eventsEventIdCancelPost} from "../../api/events/eventsEventIdCancelPost.ts";
import {isFirManager, isRegionManager} from "../../utils/userTypeGuards.ts";
import {eventsEventIdRequestCancelPost} from "../../api/events/eventsEventIdRequestCancelPost.ts";

type Props = {
    event: Event;
    onUpdate: VoidFunction;
    fullPage?: boolean;
}

export const EventActions: FC<Props> = ({ event, onUpdate, fullPage }) => {
    const { message, modal } = App.useApp();
    const { profile } = useOwnProfile();
    const { data: users } = useUsers();

    const navigate = useNavigate();

    const [approveLoading, setApproveLoading] = useState(false);
    const handleApprove = () => {
        setApproveLoading(true);
        eventsEventIdApprovePost(event.id).then(() => {
            void message.success('Согласовано');
            void onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setApproveLoading(false));
    }

    const [forwardLoading, setForwardLoading] = useState(false);
    const handleForward = (user_ids: number[]) => {
        setForwardLoading(true);
        eventsEventIdForwardPost(event.id, { user_ids }).then(() => {
            void message.success('Переадресовано');
            void onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setForwardLoading(false));
    }

    const ownRegionFirManagers = useMemo(() =>
        profile && isRegionManager(profile) ? users?.filter((u) =>
            isFirManager(u) && u.firs.some(({ region }) => region.id === profile.region.id)
        ) as (User & { role: 'fir_manager' })[] : undefined, [users, profile]
    );

    const [selectedApprovers, setSelectedApprovers] = useState<number[]>([]);
    const redirectContent = (
        <Flex gap={4} className={styles.redirectContent}>
            <Select
                options={(
                    ownRegionFirManagers?.map(({ first_name, last_name, firs, id }) => ({
                            label: `${first_name} ${last_name} (${firs?.map(({ name }) => name).join(', ')})`,
                            value: id
                        }))
                )}
                value={selectedApprovers}
                onChange={setSelectedApprovers}
                mode="multiple"
                placeholder="Иван Иванов"
                className={styles.grow}
            />
            <Button
                type="primary"
                icon={<RightOutlined />}
                loading={forwardLoading}
                disabled={!event.forwardable || !selectedApprovers.length}
                onClick={() => handleForward(selectedApprovers)}
                className={styles.noShrink}
            />
        </Flex>
    )

    const [returnLoading, setReturnLoading] = useState(false);
    const [returnReason, setReturnReason] = useState('');
    const handleReturn = () => {
        if(!returnReason) return;

        setReturnLoading(true);
        eventsEventIdReturnPost(event.id, { reason: returnReason }).then(() => {
            void message.success('Возвращено инициатору');
            void onUpdate();
            if(fullPage) navigate('/');
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setReturnLoading(false));
    }

    const returnContent = (
        <Flex gap={4} align="flex-end">
            <Input.TextArea
                rows={3}
                placeholder="Комментарий..."
                value={returnReason}
                onChange={({ target }) => setReturnReason(target.value)}
            />
            <Button
                type="primary"
                icon={<RightOutlined />}
                loading={returnLoading}
                onClick={handleReturn}
                className={styles.noShrink}
            />
        </Flex>
    );

    const [undraftLoading, setUndraftLoading] = useState(false);
    const handleUndraft = () => {
        setUndraftLoading(true);
        eventsEventIdUndraftPut(event.id).then(() => {
            void message.success('Отправлено на согласование');
            void onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(JSON.stringify(e));
        }).finally(() => setUndraftLoading(false));
    }

    const [unreturnLoading, setUnreturnLoading] = useState(false);
    const handleUnreturn = () => {
        setUnreturnLoading(true);
        eventsEventIdUnreturnPost(event.id).then(() => {
            void message.success('Отправлено на согласование');
            void onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(JSON.stringify(e));
        }).finally(() => setUnreturnLoading(false));
    }

    const [cancelLoading, setCancelLoading] = useState(false);
    const handleCancel = async () => {
        if(!await modal.confirm({ title: 'Отменить ивент?', content: 'Это действие отменить невозможно!' })) return;
        setCancelLoading(true);
        eventsEventIdCancelPost(event.id).then(() => {
            void message.success('Отменено');
            if(fullPage && event.draft) navigate('/');
            void onUpdate();
        }).catch((e) => {
            console.error(e);
            void message.error(JSON.stringify(e));
        }).finally(() => setCancelLoading(false));
    }

    const cancelButton = event.cancelable ? (
        <Button
            color="red"
            variant="outlined"
            icon={<DeleteOutlined />}
            onClick={handleCancel}
            loading={cancelLoading}
        >Отменить</Button>
    ) : null;

    const [requestCancelLoading, setRequestCancelLoading] = useState(false);
    const [requestCancelReason, setRequestCancelReason] = useState('');
    const handleRequestCancel = () => {
        if(!requestCancelReason) return;

        setRequestCancelLoading(true);
        eventsEventIdRequestCancelPost(event.id, { reason: requestCancelReason }).then(() => {
            void message.success('Запрос на отмену отправлен');
            void onUpdate();
            if(fullPage) navigate('/');
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setRequestCancelLoading(false));
    }

    const requestReturnContent = (
        <Flex gap={4} align="flex-end">
            <Input.TextArea
                rows={3}
                placeholder="Комментарий..."
                value={requestCancelReason}
                onChange={({ target }) => setRequestCancelReason(target.value)}
            />
            <Button
                type="primary"
                icon={<RightOutlined />}
                loading={requestCancelLoading}
                onClick={handleRequestCancel}
                className={styles.noShrink}
            />
        </Flex>
    );

    if(event.draft && event.created_by.id === profile?.id) {
        return (
            <Flex align="center" gap={8}>
                <Button
                    color="green"
                    variant="outlined"
                    icon={<SendOutlined />}
                    onClick={handleUndraft}
                    loading={undraftLoading}
                >Отправить на согласование</Button>
                {cancelButton}
            </Flex>
        )
    }

    if(event.returned && event.created_by.id === profile?.id) {
        return (
            <Flex align="center" gap={8}>
                <Button
                    color="green"
                    variant="outlined"
                    icon={<SendOutlined />}
                    onClick={handleUnreturn}
                    loading={unreturnLoading}
                >Отправить на согласование</Button>
                {cancelButton}
            </Flex>
        )
    }

    if(!event.approvable) {
        if(cancelButton) return cancelButton;
        if(event.cancel_requestable) {
            return (
                <Popover
                    trigger="click"
                    content={requestReturnContent}
                    placement="bottom"
                >
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        loading={requestCancelLoading}
                    >Запросить отмену</Button>
                </Popover>
            )
        }
        return null;
    }

    return (
        <Flex align="center" gap={8}>
            <Button
                color="green"
                variant="outlined"
                icon={<CheckCircleOutlined />}
                onClick={handleApprove}
                loading={approveLoading}
            >Согласовать</Button>
            <Popover
                trigger="click"
                title="Выберите пользователей"
                content={redirectContent}
                placement="bottom"
            >
                <Button
                    icon={<RightCircleOutlined />}
                    disabled={!event.forwardable}
                    loading={forwardLoading}
                >Перенаправить</Button>
            </Popover>
            <Popover
                trigger="click"
                content={returnContent}
                placement="bottom"
            >
                <Button
                    danger
                    icon={<StopOutlined />}
                    loading={returnLoading}
                >Вернуть инициатору</Button>
            </Popover>
            {cancelButton}
        </Flex>
    )
}